/**
 * TalentInterestButton — lightweight "I'm interested in working here"
 * pill for the public EmployerDetail page. Wires student-side of the
 * two-way match: if the student ALREADY has a talent profile, a POST
 * to /talent/interest/student is fired; otherwise a nudge routes
 * them to /me/talent-profile first.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Handshake, Sparkles, ArrowRight, Loader2 } from "lucide-react";


export default function TalentInterestButton({ firmSlug, firmName }) {
  const { user, openSignIn } = useAuth();
  const [state, setState] = useState({ hasProfile: null, interestId: null, mutual: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: p }, { data: i }] = await Promise.all([
          api.get("/talent/profile/me"),
          api.get("/talent/interests/mine"),
        ]);
        const existing = (i.as_student || []).find(r => r.firm_slug === firmSlug);
        setState({
          hasProfile: !!p.profile,
          interestId: existing?.id || null,
          mutual: existing?.status === "mutual",
        });
      } catch { /* silent */ }
    })();
  }, [user, firmSlug]);

  const declare = async () => {
    if (!user) { openSignIn?.(); return; }
    if (!state.hasProfile) {
      // Nudge them to build a profile first.
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/talent/interest/student", {
        firm_slug: firmSlug,
      });
      toast.success(data.mutual
        ? "Mutual match! The firm has your details."
        : `We told ${firmName || "this firm"} you're interested`);
      const { data: i } = await api.get("/talent/interests/mine");
      const existing = (i.as_student || []).find(r => r.firm_slug === firmSlug);
      setState({
        hasProfile: true,
        interestId: existing?.id || null,
        mutual: existing?.status === "mutual",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.delete(`/talent/interest?id=${encodeURIComponent(state.interestId)}`);
      setState({ ...state, interestId: null, mutual: false });
      toast.success("Withdrawn");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  // Not signed in — soft CTA
  if (!user) {
    return (
      <button
        onClick={openSignIn}
        data-testid="talent-interest-signin-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-md border border-[#4C1D95]/40 bg-white px-4 py-2.5 text-[13px] font-medium text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition"
      >
        <Handshake className="h-4 w-4" />
        I'm interested in working here — sign in to save
      </button>
    );
  }

  // Signed in but no profile yet — nudge to build one
  if (state.hasProfile === false) {
    return (
      <Link
        to="/me/talent-profile"
        data-testid="talent-interest-profile-cta"
        className="mt-6 inline-flex items-center gap-2 rounded-md border border-[#4C1D95]/40 bg-white px-4 py-2.5 text-[13px] font-medium text-[#4C1D95] hover:bg-[#4C1D95] hover:text-white transition"
      >
        <Handshake className="h-4 w-4" />
        I'm interested — build your talent profile first
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    );
  }

  // Already declared + mutual
  if (state.mutual) {
    return (
      <div
        data-testid="talent-interest-mutual"
        className="mt-6 inline-flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-[13px] font-medium text-emerald-800"
      >
        <Sparkles className="h-4 w-4" />
        Mutual match with {firmName || "this firm"} — check{" "}
        <Link to="/me/talent-profile" className="underline">
          your talent profile
        </Link>
      </div>
    );
  }

  // Signed in, has profile, declared but not mutual yet
  if (state.interestId) {
    return (
      <button
        onClick={withdraw}
        disabled={busy}
        data-testid="talent-interest-withdraw"
        className="mt-6 inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-4 py-2.5 text-[13px] font-medium text-stone-700 hover:bg-stone-50 transition"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Handshake className="h-4 w-4" />}
        Waiting for {firmName || "firm"} — cancel interest
      </button>
    );
  }

  // Signed in, has profile, has NOT declared → the main CTA
  return (
    <button
      onClick={declare}
      disabled={busy}
      data-testid="talent-interest-declare"
      className="mt-6 inline-flex items-center gap-2 rounded-md border border-[#4C1D95] bg-[#4C1D95] px-4 py-2.5 text-[13px] font-medium text-white hover:bg-[#3B0764] transition"
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Handshake className="h-4 w-4" />}
      I'm interested in working here
    </button>
  );
}
